import dayjs from "dayjs";
import dayjsDuration from "dayjs/plugin/duration";
import dayjsRelativeTime from "dayjs/plugin/relativeTime";
import dayjsCustomParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/ja";

dayjs.extend(dayjsDuration);
dayjs.extend(dayjsRelativeTime);
dayjs.extend(dayjsCustomParseFormat);

const DEFAULT_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss Z"

// 引数で渡された jQuery オブジェクト $node に対して経過時間を描画する。 node の datetime 属性に日時が指定されている必要
// がある。デフォルトでは経過時間に応じて「◯分」や「◯時間×分」などと表示する。 format オプションで経過時間の表示フォー
// マットを指定できる。デフォルトでは5秒ごとに再描画される。 interval オプションで描画間隔を指定できる。
// 指定できるフォーマット: https://day.js.org/docs/en/durations/format
// フォーマットを指定する場合、H は最大23、 m は最大59など癖があるので注意。
export function renderElapsedTime($node, options = {}) {
  if ($node == null || $node.length === 0) {
    console.error("Node doesn't exist:", $node);
    return;
  }

  const interval = options.interval ?? 5000;

  // FirefoxとSafariが日時文字列の解析に失敗してしまうのでフォーマットを指定してパースしている。
  // cf. https://github.com/iamkun/dayjs/issues/631
  const datetime = dayjs($node.attr("datetime"), DEFAULT_DATETIME_FORMAT);

  render();

  function render() {
    // node がDOMツリーから削除されたあとは処理しない。
    if (!$node.prop("isConnected")) {
      return;
    }

    const elapsedTime = calculateElapsedTime();
    const text = formatElapsedTime(elapsedTime, options);

    $node.text(text);

    // interval 秒後に再度 render() を呼び出す。定期的に再描画するため。
    setTimeout(render, interval);
  }

  function calculateElapsedTime() {
    const now = dayjs();
    return dayjs.duration(now.diff(datetime));
  }

}


// 引数で渡された jQuery オブジェクト $node に対して経過時間を描画する。 node の from_datetime/to_datetime 属性に日時が指定されている必要
// がある。デフォルトでは経過時間に応じて「◯分」や「◯時間×分」などと表示する。
// 指定できるフォーマット: https://day.js.org/docs/en/durations/format
// フォーマットを指定する場合、H は最大23、 m は最大59など癖があるので注意。
export function renderFixedElapsedTime($node, options = {}) {

  if ($node == null || $node.length === 0) {
    console.error("Node doesn't exist:", $node);
    return;
  }

  // FirefoxとSafariが日時文字列の解析に失敗してしまうのでフォーマットを指定してパースしている。
  // cf. https://github.com/iamkun/dayjs/issues/631
  const from_datetime = dayjs($node.attr("from_datetime"), DEFAULT_DATETIME_FORMAT);
  const to_datetime = dayjs($node.attr("to_datetime"), DEFAULT_DATETIME_FORMAT);
  
  const elapsedTime = dayjs.duration(to_datetime.diff(from_datetime));
  const text = formatElapsedTime(elapsedTime, options);

  $node.text(text);
}

function formatElapsedTime(elapsedTime, options = {}) {
  // オプションでフォーマットが指定されていたらそれを固定で使う。
  if (options.format) {
    return elapsedTime.format(options.format);
  } else {
    // 1ヶ月以上経過している場合はdayjsの humanize() を利用する。1ヶ月未満の経過時間に関しては自前でフォーマットを組み
    // 立てる。dayjsの humanize() は例えば経過時間45秒で「1分」、経過時間50分で「1時間」と表記するなど感覚とズレる（少
    // し多めの時間で表記されてしまう == 「約」を付けると感覚と一致する）ため。
    if (elapsedTime.asMonths() >= 1) {
      return elapsedTime.locale("ja").humanize();
    } else {
      // 以下、自前のフォーマット組み立て処理。結果として出力される時間の例: 「0分」「1分」「12分」「1時間」「1時間2分」
      // 「12時間」「12時間3分」「1日」「1日2時間」「1日2分」「1日2時間3分」「12日」「12日13時間14分」
      let format = "";
      const labels = options.labels;

      if (elapsedTime.hours() >= 1) {
        format = `H${labels.hours}${format}`;
      }

      if (elapsedTime.days() >= 1) {
        format = `D${labels.days}${format}`;
      }

      // 分数が1以上の場合は「◯分」を表示する。
      if (elapsedTime.minutes() >= 1) {
        format = `${format}m${labels.minutes}`;
      } else {
        // 分数が1未満の場合、「format が空」 == 「時間数も日数も1未満」 == 「総経過時間が1分未満」の場合
        // にのみ「0分」と表記する。
        if (format.length === 0) {
          format = `m${labels.minutes}`;
        }
      }

      return elapsedTime.format(format);
    }
  }
}
